// src/pages/MrpContainerDetails.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { normalizeDateInput, toDisplayDate } from "../utils/dataFormatters";
import DateRangeFilter from "../components/common/DateRangeFilter";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { filterRowsByAccess } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const COLUMNS = [
  { key: "status", label: "Status" },
  { key: "cust_code", label: "Cust Code" },
  { key: "id", label: "ID" },
  { key: "_id", label: "_ID" },
  { key: "order_customerinvoicecode", label: "Order No" },
  { key: "tariff_so", label: "MBL No" },
  { key: "cabinet_number", label: "Container No" },
  { key: "etd", label: "ETD" },
  { key: "eta", label: "ETA" },
  { key: "retail_sku", label: "Retail SKU" },
  { key: "vessel_id", label: "VESSEL_ID" },
  { key: "product_pl_id", label: "Product Code" },
  { key: "productname", label: "Product Name-EN" },
  { key: "warehouse", label: "WHSE Code" },
  { key: "product_cn_name", label: "Product Name-CN" },
  { key: "invoice_pic_all", label: "Pieces" },
  { key: "number_boxes", label: "Boxes" },
];

const STATUS_STYLES = {
  Booked: "bg-cyan-100 text-cyan-700 border border-cyan-200",
  Planning: "bg-gray-100 text-gray-700 border border-gray-200",
  Offshore: "bg-blue-100 text-blue-700 border border-blue-200",
  Arrival: "bg-amber-100 text-amber-700 border border-amber-200",
  "Appt Made": "bg-purple-100 text-purple-700 border border-purple-200",
  Delivered: "bg-green-100 text-green-700 border border-green-200",
  Emptied: "bg-violet-100 text-violet-700 border border-violet-200",
  Closed: "bg-red-100 text-red-700 border border-red-200",
};

const STATUS_LIST = Object.keys(STATUS_STYLES);
const EMPTY_VALUE = "__EMPTY__";
const MULTI_FILTER_KEYS = new Set(["retail_sku", "product_pl_id", "productname", "warehouse"]);
const PAGE_OPTIONS = [25, 50, 100, "All"];
const CONTROL_COLUMN_WIDTH = 52;
const MIN_TABLE_ROWS = 20;
const TABLE_ROW_HEIGHT_PX = 44;
const TABLE_HEADER_HEIGHT_PX = 52;
const MIN_TABLE_HEIGHT = TABLE_HEADER_HEIGHT_PX + MIN_TABLE_ROWS * TABLE_ROW_HEIGHT_PX;
const DEFAULT_COLUMN_WIDTHS = {
  status: 120,
  cust_code: 120,
  id: 110,
  _id: 220,
  order_customerinvoicecode: 200,
  tariff_so: 160,
  cabinet_number: 180,
  etd: 140,
  eta: 140,
  retail_sku: 160,
  vessel_id: 120,
  product_pl_id: 160,
  productname: 220,
  product_cn_name: 200,
  warehouse: 140,
  invoice_pic_all: 140,
  number_boxes: 140,
};
const getDefaultWidth = (key) => DEFAULT_COLUMN_WIDTHS[key] || 180;
const DEFAULT_VISIBLE_COLUMNS = [
  "status",
  "cust_code",
  "order_customerinvoicecode",
  "tariff_so",
  "cabinet_number",
  "etd",
  "eta",
  "retail_sku",
  "product_pl_id",
  "productname",
  "warehouse",
  "invoice_pic_all",
  "number_boxes",
];

const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const arraysEqual = (a = [], b = []) => a.length === b.length && a.every((val, idx) => val === b[idx]);

const normaliseFilterValue = (value) => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === "number") {
    if (Number.isNaN(value)) return EMPTY_VALUE;
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? EMPTY_VALUE : trimmed;
};

const displayLabelForToken = (raw, token) => {
  if (token === EMPTY_VALUE) return "(Blank)";
  if (raw === null || raw === undefined) return String(token);
  return String(raw);
};

const formatTimestampForFilename = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const numberLike = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return value;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const compareForSort = (left, right) => {
  const leftNumber = numberLike(left);
  const rightNumber = numberLike(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }
  const leftString = left === null || left === undefined ? "" : String(left).toLowerCase();
  const rightString = right === null || right === undefined ? "" : String(right).toLowerCase();
  return leftString.localeCompare(rightString);
};

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (!v) return false;
    return ["1", "true", "t", "yes", "y", "on"].includes(v);
  }
  return false;
};

const deriveStatus = (row = {}) => {
  const manual = String(row?.status ?? "").trim();
  if (manual === "Returned" || manual === "Closed") return "Closed";
  if (toBool(row?.m_closed)) return "Closed";
  const has = (key) => !!String(row?.[key] ?? "").trim();
  const rawCode = row?.status_code ?? row?.statusCode ?? row?.order_status ?? row?.orderStatus;
  const statusCode = (() => {
    if (rawCode === null || rawCode === undefined || rawCode === "") return null;
    const parsed = Number(String(rawCode).replace(/[^0-9-]/g, ""));
    return Number.isNaN(parsed) ? null : parsed;
  })();
  if (has("returned_date")) return "Closed";
  if (has("emptied_dt")) return "Emptied";
  if (has("delivered_dt")) return "Delivered";
  if (has("appt_date")) return "Appt Made";
  if (has("arrived")) return "Arrival";
  if (statusCode === 3) return "Offshore";
  if (statusCode === 2) return "Booked";
  if (statusCode === 1) return "Planning";
  return manual || "Planning";
};

const statusClassName = (value) => {
  const key = typeof value === "string" ? value.trim() : "";
  return STATUS_STYLES[key] || STATUS_STYLES.Planning;
};

const StatusBadge = ({ value }) => {
  const display = typeof value === "string" && value.trim() ? value.trim() : "Planning";
  return <span className={`inline-block px-2 py-0.5 text-xs rounded ${statusClassName(display)}`}>{display}</span>;
};

const extractProductName = (row = {}) => {
  if (row.productname) return row.productname;
  if (row.productName) return row.productName;
  if (row.product_name) return row.product_name;
  return "";
};

const normaliseOrderKey = (value) => String(value ?? "").trim().toLowerCase();

const createInitialFilters = () => {
  const next = {};
  COLUMNS.forEach(({ key }) => {
    next[key] = [];
  });
  next.etdFrom = "";
  next.etdTo = "";
  next.etaFrom = "";
  next.etaTo = "";
  return next;
};

export default function MrpContainerDetails() {
  const accessVersion = useAccessOverrideVersion();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const accessibleRows = useMemo(() => {
    void accessVersion;
    return filterRowsByAccess(rows, {
      columns: COLUMNS.map((column) => column.key),
      disableCust: true,
      disableWhs: true,
    });
  }, [rows, accessVersion]);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("order_customerinvoicecode");
  const [sortDirection, setSortDirection] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState(() => [...DEFAULT_VISIBLE_COLUMNS]);
  const [widths, setWidths] = useState(() =>
    Object.fromEntries(COLUMNS.map(({ key }) => [key, getDefaultWidth(key)]))
  );

  const columnOptions = useMemo(() => COLUMNS.map(({ key, label }) => ({ value: key, label })), []);
  const dragRef = useRef({ key: "", startX: 0, startW: 0 });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [myContainersResp, rawVesselsResp, rawContainersResp, productsResp] = await Promise.all([
        fetch(`${API_BASE}/my-containers`),
        fetch(`${API_BASE}/api/public/raw-vessels`),
        fetch(`${API_BASE}/api/public/raw-vessel-containers`),
        fetch(`${API_BASE}/mrp/products`),
      ]);

      const [myContainersJson, rawVesselsJson, rawContainersJson, productsJson] = await Promise.all([
        myContainersResp.json().catch(() => ({})),
        rawVesselsResp.json().catch(() => ({})),
        rawContainersResp.json().catch(() => ({})),
        productsResp.json().catch(() => ({})),
      ]);

      if (!myContainersResp.ok || !myContainersJson?.ok) {
        throw new Error(myContainersJson?.error || "Failed to load My Containers data.");
      }
      if (!rawVesselsResp.ok || !rawVesselsJson?.ok) {
        throw new Error(rawVesselsJson?.error || "Failed to load raw vessels data.");
      }
      if (!rawContainersResp.ok || !rawContainersJson?.ok) {
        throw new Error(rawContainersJson?.error || "Failed to load raw vessel containers data.");
      }
      if (!productsResp.ok || !productsJson?.ok) {
        throw new Error(productsJson?.error || "Failed to load MRP products.");
      }

      const orderMeta = new Map();
      (myContainersJson.rows || []).forEach((row = {}) => {
        const key = normaliseOrderKey(row.order_no || row.orderNo || row.order_customerinvoicecode);
        if (!key) return;
        const normalized = { ...row, m_closed: toBool(row.m_closed) };
        const etdIso = normalizeDateInput(normalized.etd, false) || "";
        const etaIso = normalizeDateInput(normalized.eta, false) || "";
        orderMeta.set(key, {
          status: deriveStatus(normalized),
          custCode: String(normalized.cust_code ?? normalized.custCode ?? "").trim(),
          etdRaw: etdIso,
          etaRaw: etaIso,
          etdDisplay: toDisplayDate(etdIso || normalized.etd),
          etaDisplay: toDisplayDate(etaIso || normalized.eta),
          warehouse: String(normalized.warehouse ?? "").trim(),
        });
      });

      const vesselsById = new Map();
      const vesselOrderById = new Map();
      (rawVesselsJson.rows || []).forEach((row = {}) => {
        const vesselId = row.id;
        if (vesselId === null || vesselId === undefined) return;
        vesselsById.set(vesselId, row);
        const orderCode = String(
          row.order_customerinvoicecode ?? row.ORDER_CUSTOMERINVOICECODE ?? ""
        ).trim();
        vesselOrderById.set(vesselId, orderCode);
      });

      const productMap = new Map();
      (productsJson.rows || []).forEach((row = {}) => {
        const code = String(row.product_code ?? "").trim().toLowerCase();
        if (!code) return;
        productMap.set(code, row);
      });

      const merged = [];
      (rawContainersJson.rows || []).forEach((containerRow = {}) => {
        const vesselId = containerRow.vessel_id ?? containerRow.vesselId;
        if (vesselId === null || vesselId === undefined) return;
        const vesselRow = vesselsById.get(vesselId);
        if (!vesselRow) return;

        const orderCode = vesselOrderById.get(vesselId) || "";
        const orderKey = normaliseOrderKey(orderCode);
        const meta = orderKey ? orderMeta.get(orderKey) : null;
        const status = meta?.status || "Planning";
        const productCodeValue = containerRow.product_pl_id ?? containerRow.productPlId ?? "";
        const productMeta = productMap.get(String(productCodeValue).trim().toLowerCase());

        merged.push({
          rowIdentity:
            containerRow.id ??
            containerRow._id ??
            (containerRow._index !== null &&
            containerRow._index !== undefined &&
            containerRow._index !== ""
              ? `${vesselId}-${containerRow._index}`
              : `${vesselId}-${merged.length}`),
          status,
          cust_code: meta?.custCode || "",
          id: vesselRow.id ?? "",
          _id: vesselRow._id ?? vesselRow.id ?? "",
          order_customerinvoicecode: orderCode,
          tariff_so: vesselRow.tariff_so ?? vesselRow.tariffSo ?? "",
          cabinet_number: vesselRow.cabinet_number ?? vesselRow.cabinetNumber ?? "",
          etd: meta?.etdDisplay || "",
          eta: meta?.etaDisplay || "",
          etdRaw: meta?.etdRaw || "",
          etaRaw: meta?.etaRaw || "",
          retail_sku: productMeta?.retail_sku ?? "",
          vessel_id: vesselId,
          product_pl_id: containerRow.product_pl_id ?? containerRow.productPlId ?? "",
          productname: extractProductName(containerRow),
          product_cn_name: containerRow.product_cn_name ?? containerRow.productCnName ?? "",
          warehouse: meta?.warehouse || "",
          invoice_pic_all: containerRow.invoice_pic_all ?? containerRow.invoicePicAll ?? "",
          number_boxes: containerRow.number_boxes ?? containerRow.numberBoxes ?? "",
        });
      });

      merged.sort((a, b) => {
        const orderCompare = compareForSort(a.order_customerinvoicecode, b.order_customerinvoicecode);
        if (orderCompare !== 0) return orderCompare;
        return compareForSort(a.product_pl_id, b.product_pl_id);
      });

      setRows(merged);
      setLastUpdated(new Date());
      setPage(1);
    } catch (err) {
      setRows([]);
      setError(err.message || "Failed to load container details.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const filterOptions = useMemo(() => {
    const optionsByKey = {};
    const seenMap = {};
    COLUMNS.forEach(({ key }) => {
      optionsByKey[key] = [];
      seenMap[key] = new Set();
    });

    rows.forEach((row) => {
      COLUMNS.forEach(({ key }) => {
        const raw = row[key];
        const token = normaliseFilterValue(raw);
        if (seenMap[key].has(token)) return;
        seenMap[key].add(token);
        optionsByKey[key].push({ value: token, label: displayLabelForToken(raw, token) });
      });
    });

    const statusOptions = STATUS_LIST.map((status) => {
      const token = normaliseFilterValue(status);
      return { value: token, label: status };
    });
    const combinedStatus = [...statusOptions, ...(optionsByKey.status || [])];
    if ((optionsByKey.status || []).some((option) => option.value === EMPTY_VALUE)) {
      combinedStatus.push({ value: EMPTY_VALUE, label: "(Blank)" });
    }
    optionsByKey.status = combinedStatus.filter(
      (option, index, array) => index === array.findIndex((item) => item.value === option.value)
    );

    Object.keys(optionsByKey).forEach((key) => {
      if (key === "status") return;
      optionsByKey[key].sort((a, b) => a.label.localeCompare(b.label));
      if (MULTI_FILTER_KEYS.has(key)) {
        const blankIndex = optionsByKey[key].findIndex((option) => option.value === EMPTY_VALUE);
        if (blankIndex > 0) {
          const [blankOption] = optionsByKey[key].splice(blankIndex, 1);
          optionsByKey[key].unshift(blankOption);
        }
      }
    });

    return optionsByKey;
  }, [rows]);

  const displayColumns = useMemo(
    () => COLUMNS.filter(({ key }) => visibleColumns.includes(key)),
    [visibleColumns]
  );

  const onVisibleColumnsChange = useCallback(
    (next) => {
      const list = toArray(next);
      if (!list.length) return;
      const ordered = COLUMNS.map(({ key }) => key).filter((key) => list.includes(key));
      setVisibleColumns(ordered);
      if (sortKey && !ordered.includes(sortKey)) {
        setSortKey("");
        setSortDirection(0);
      }
    },
    [sortKey, setSortKey, setSortDirection]
  );

  const onDragging = useCallback((event) => {
    const { key, startX, startW } = dragRef.current;
    if (!key) return;
    const dx = event.clientX - startX;
    const nextWidth = Math.max(80, startW + dx);
    setWidths((prev) => {
      if (prev[key] === nextWidth) return prev;
      return { ...prev, [key]: nextWidth };
    });
  }, []);

  const onDragEnd = useCallback(() => {
    window.removeEventListener("mousemove", onDragging);
    window.removeEventListener("mouseup", onDragEnd);
    dragRef.current = { key: "", startX: 0, startW: 0 };
  }, [onDragging]);

  const onDragStart = (event, key) => {
    dragRef.current = { key, startX: event.clientX, startW: widths[key] || getDefaultWidth(key) };
    window.addEventListener("mousemove", onDragging);
    window.addEventListener("mouseup", onDragEnd);
    event.preventDefault();
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onDragging);
      window.removeEventListener("mouseup", onDragEnd);
    };
  }, [onDragging, onDragEnd]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const current = prev[key];
      if (Array.isArray(value)) {
        if (arraysEqual(Array.isArray(current) ? current : [], value)) return prev;
        return { ...prev, [key]: value };
      }
      if (current === value) return prev;
      return { ...prev, [key]: value };
    });
    setPage(1);
  }, [setPage]);

  const resetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setPage(1);
  }, [setPage]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const activeFilters = COLUMNS.filter(({ key }) => (filters[key] || []).length > 0);
    const rangeFilters = ["etd", "eta"];
    return accessibleRows.filter((row) => {
      for (const { key } of activeFilters) {
        if (rangeFilters.includes(key)) continue;
        const selected = filters[key] || [];
        if (!selected.length) continue;
        const token = normaliseFilterValue(row[key]);
        if (!selected.includes(token)) return false;
      }
      for (const key of rangeFilters) {
        const from = filters[`${key}From`] || "";
        const to = filters[`${key}To`] || "";
        if (!from && !to) continue;
        const rawValue = row[`${key}Raw`] || row[key] || "";
        if (!rawValue) return false;
        const valueTime = new Date(rawValue).getTime();
        if (Number.isNaN(valueTime)) return false;
        if (from) {
          const fromTime = new Date(from).getTime();
          if (!Number.isNaN(fromTime) && valueTime < fromTime) return false;
        }
        if (to) {
          const toTime = new Date(to).getTime();
          if (!Number.isNaN(toTime) && valueTime > toTime) return false;
        }
      }
      if (!term) return true;
      return COLUMNS.some(({ key }) => {
        const value = row[key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [accessibleRows, filters, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortKey || sortDirection === 0) return filteredRows;
    const next = [...filteredRows];
    next.sort((a, b) => {
      const left = sortKey === "etd" ? a.etdRaw || a.etd
        : sortKey === "eta" ? a.etaRaw || a.eta
        : a[sortKey];
      const right = sortKey === "etd" ? b.etdRaw || b.etd
        : sortKey === "eta" ? b.etaRaw || b.eta
        : b[sortKey];
      const base = compareForSort(left, right);
      return sortDirection === -1 ? -base : base;
    });
    return next;
  }, [filteredRows, sortKey, sortDirection]);

  const onPivot = useCallback(() => {
    if (!sortedRows.length) return;
    navigate("/mrp/container-details/pivot", { state: { rows: sortedRows } });
  }, [navigate, sortedRows]);

  const totalRows = filteredRows.length;
  const totalPages = useMemo(() => {
    if (!totalRows) return 1;
    const size = pageSize === "All" ? totalRows || 1 : pageSize;
    return Math.max(1, Math.ceil(totalRows / size));
  }, [totalRows, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    if (pageSize === "All") return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const startIdx = totalRows === 0 ? 0 : pageSize === "All" ? 0 : (page - 1) * pageSize;
  const endIdx =
    totalRows === 0
      ? 0
      : pageSize === "All"
      ? totalRows
      : Math.min(totalRows, startIdx + (typeof pageSize === "number" ? pageSize : totalRows));

  const toggleSort = (key) => {
    if (sortKey !== key || !sortKey) {
      setSortKey(key);
      setSortDirection(1);
      return;
    }
    const nextDirection = sortDirection === 1 ? -1 : sortDirection === -1 ? 0 : 1;
    if (nextDirection === 0) {
      setSortDirection(0);
      setSortKey("");
    } else {
      setSortDirection(nextDirection);
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key || sortDirection === 0) {
      return (
        <svg className="h-3.5 w-3.5 opacity-40" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 7l3-3 3 3H7zM13 13l-3 3-3-3h6z" />
        </svg>
      );
    }
    if (sortDirection === 1) {
      return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 11l3 3 3-3H7z" />
        </svg>
      );
    }
    return (
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M7 9l3-3 3 3H7z" />
      </svg>
    );
  };

  const onExport = useCallback(() => {
    if (!sortedRows.length) return;
    const header = COLUMNS.map((column) => column.label);
    const dataRows = sortedRows.map((row) =>
      COLUMNS.map((column) => {
        const value = row[column.key];
        return value === null || value === undefined ? "" : value;
      })
    );
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Container Details");
    const filename = `Container Details ${formatTimestampForFilename(new Date())}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [sortedRows]);

  const exportDisabled = sortedRows.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <h1 className="text-2xl font-semibold text-slate-900">Container Details</h1>
          <span className="text-sm text-slate-600 whitespace-nowrap">
            Filtered {totalRows.toLocaleString()} rows
          </span>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search all columns…"
              className="w-64 rounded border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 7V3H7v4H4l6 6 6-6h-3z" />
                <path d="M4 15h12v2H4z" />
              </svg>
              Export
            </button>
            <button
              type="button"
              onClick={fetchData}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-40"
          disabled={loading}
        >
          <svg
            className={`h-4 w-4 ${loading ? "animate-spin text-blue-500" : "text-slate-500"}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4 10a6 6 0 0110.39-4.19l1.42-1.42A8 8 0 102 10h2zm12-4h-4v2h4V6z"
              clipRule="evenodd"
            />
          </svg>
          Refresh
        </button>
        <button
          type="button"
          onClick={onPivot}
          className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={loading || !sortedRows.length}
        >
          Pivot
        </button>
      </div>
    </div>
        <div className="flex flex-col items-end gap-2 text-sm text-slate-500">
          <UserAccessSummary className="text-xs" />
          <div>
            {lastUpdated
              ? `Last updated ${lastUpdated.toLocaleString()}`
              : loading
              ? "Loading..."
              : "Awaiting data"}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <MultiSelectDropdown
              value={visibleColumns}
              options={columnOptions}
              onChange={onVisibleColumnsChange}
              placeholder="Columns"
              buttonClassName="text-xs min-w-[150px]"
            />
            <span>Rows:</span>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={pageSize}
              onChange={(event) => {
                const value = event.target.value;
                setPageSize(value === "All" ? "All" : parseInt(value, 10));
                setPage(1);
              }}
            >
              {PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              {"<"}
            </button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              aria-label="Next page"
            >
              {">"}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              aria-label="Last page"
            >
              {">>"}
            </button>
            <span className="text-slate-500 tabular-nums">
              {totalRows ? `${startIdx + 1} - ${endIdx} of ${totalRows}` : "0"}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div
        className="relative max-h-[calc(100vh-140px)] overflow-auto rounded border border-slate-200 bg-white shadow-sm"
        style={{ minHeight: `${MIN_TABLE_HEIGHT}px` }}
      >
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th
                className="sticky top-0 z-20 bg-white border-b px-3 py-2 text-left text-sm font-semibold"
                style={{ width: CONTROL_COLUMN_WIDTH, minWidth: CONTROL_COLUMN_WIDTH }}
              />
              {displayColumns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: widths[column.key], minWidth: widths[column.key] }}
                  className="sticky top-0 z-20 bg-white border-b px-3 py-2 text-left text-sm font-semibold relative select-none"
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className="flex items-center gap-1 pr-3 text-left"
                  >
                    <span>{column.label}</span>
                    {sortIcon(column.key)}
                  </button>
                  <span
                    onMouseDown={(event) => onDragStart(event, column.key)}
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                    title="Drag to resize"
                    style={{ userSelect: "none" }}
                  />
                </th>
              ))}
            </tr>
            <tr className="sticky top-[42px] z-10 bg-white">
              <th
                className="bg-emerald-50 border-b px-2 py-1"
                style={{ width: CONTROL_COLUMN_WIDTH, minWidth: CONTROL_COLUMN_WIDTH }}
              >
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={resetFilters}
                    title="Reset filters"
                    aria-label="Reset filters"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 1 1-3-6.7L21 7" />
                      <path d="M21 3v4h-4" />
                    </svg>
                  </button>
                </div>
              </th>
              {displayColumns.map((column, index) => {
                const options = filterOptions[column.key] || [];
                let control;
                if (column.key === "etd" || column.key === "eta") {
                  control = (
                    <DateRangeFilter
                      value={{
                        from: filters[`${column.key}From`] || "",
                        to: filters[`${column.key}To`] || "",
                      }}
                      onChange={({ from, to }) => {
                        setFilter(`${column.key}From`, from || "");
                        setFilter(`${column.key}To`, to || "");
                      }}
                      placeholder="From-To"
                    />
                  );
                } else {
                  const useMultiFilter = MULTI_FILTER_KEYS.has(column.key);
                  control = (
                    <MultiSelectDropdown
                      value={filters[column.key]}
                      options={options}
                      onChange={(next) => setFilter(column.key, next)}
                      placeholder="All"
                      buttonClassName="text-xs"
                      showSelectAll={useMultiFilter}
                      showSearch={useMultiFilter}
                    />
                  );
                }
                return (
                  <th
                    key={column.key}
                    className="bg-emerald-50 border-b px-2 py-1"
                    style={{ width: widths[column.key], minWidth: widths[column.key] }}
                  >
                    {control}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={displayColumns.length + 1}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  Loading container details…
                </td>
              </tr>
            ) : paginatedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={displayColumns.length + 1}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  No records match the current filters.
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, index) => (
                <tr key={row.rowIdentity || `${row.order_customerinvoicecode || "row"}-${row.product_pl_id || row.vessel_id || index}`} className="border-b">
                  <td
                    className="px-3 py-2"
                    style={{ width: CONTROL_COLUMN_WIDTH, minWidth: CONTROL_COLUMN_WIDTH }}
                  />
                  {displayColumns.map((column) => {
                    const value = row[column.key];
                    if (column.key === "status") {
                      return (
                        <td
                          key={column.key}
                          className="px-3 py-2 text-sm text-slate-700"
                          style={{ width: widths[column.key], minWidth: widths[column.key] }}
                        >
                          <StatusBadge value={value} />
                        </td>
                      );
                    }
                    return (
                      <td
                        key={column.key}
                        className="px-3 py-2 text-sm text-slate-700"
                        style={{ width: widths[column.key], minWidth: widths[column.key] }}
                      >
                        {value === null || value === undefined || value === "" ? "\u2014" : value}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MultiSelectDropdown({
  value = [],
  options = [],
  onChange,
  placeholder = "All",
  buttonClassName = "",
  showSelectAll = false,
  showSearch = false,
}) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const selectedValues = toArray(value);
  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      if (!option) return;
      const val = option.value;
      if (val === undefined || val === null) return;
      map.set(val, option.label ?? option.value ?? "");
    });
    return map;
  }, [options]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const btn = buttonRef.current;
    if (!btn) return;
    const measure = () => {
      const { width } = btn.getBoundingClientRect();
      if (width && Math.abs(width - menuWidth) > 0.5) {
        setMenuWidth(width);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, menuWidth]);

  const optionOrder = useMemo(() => options.map((option) => option.value), [options]);
  const hasOptions = optionOrder.length > 0;
  const allSelected = hasOptions && optionOrder.every((optionValue) => selectedValues.includes(optionValue));
  const filteredOptions = useMemo(() => {
    if (!showSearch) return options;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const label = String(option?.label ?? option?.value ?? "").toLowerCase();
      return label.includes(term);
    });
  }, [options, searchTerm, showSearch]);
  const buildNextList = (nextSet) => {
    const ordered = optionOrder.filter((item) => nextSet.has(item));
    selectedValues.forEach((item) => {
      if (!nextSet.has(item)) return;
      if (optionOrder.includes(item)) return;
      ordered.push(item);
    });
    return ordered;
  };

  const toggleOption = (optionValue) => {
    const nextSet = new Set(selectedValues);
    if (nextSet.has(optionValue)) nextSet.delete(optionValue);
    else nextSet.add(optionValue);
    const nextList = buildNextList(nextSet);
    onChange?.(nextList);
  };

  const clearSelection = () => {
    if (selectedValues.length === 0) return;
    onChange?.([]);
  };
  const selectAll = () => {
    if (!hasOptions || allSelected) return;
    onChange?.(optionOrder.slice());
  };

  const labels = selectedValues.map((val) => labelMap.get(val) || String(val || ""));
  let summary = placeholder;
  if (labels.length === 1) summary = labels[0] || placeholder;
  else if (labels.length === 2) summary = labels.join(", ");
  else if (labels.length > 2) summary = `${labels.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`flex w-full items-center justify-between gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${buttonClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-slate-700">{summary || placeholder}</span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 z-20 mt-1 rounded-md border border-slate-200 bg-white shadow-lg"
          style={{
            minWidth: menuWidth || undefined,
            width: menuWidth || undefined,
          }}
        >
          {(showSelectAll || showSearch) && (
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white">
              <div className="flex items-center justify-between px-3 py-1.5 text-xs">
                {showSelectAll ? (
                  <button
                    type="button"
                    onClick={selectAll}
                    className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!hasOptions || allSelected}
                  >
                    Select All
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={selectedValues.length === 0}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="text-blue-600 hover:underline"
                  >
                    Close
                  </button>
                </div>
              </div>
              {showSearch && (
                <div className="border-t border-slate-200 px-3 py-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search..."
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          )}
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => {
                const val = option.value;
                const label = option.label ?? option.value ?? "";
                const checked = selectedValues.includes(val);
                return (
                  <label
                    key={val}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-blue-600"
                      checked={checked}
                      onChange={() => toggleOption(val)}
                    />
                    <span className="truncate">{label || "(Blank)"}</span>
                  </label>
                );
              })
            ) : (
              <div className="px-3 py-2 text-xs text-slate-500">No options found</div>
            )}
          </div>
          {!showSelectAll && !showSearch && (
            <div className="flex items-center justify-between border-t border-slate-200 px-3 py-1.5 text-xs">
              <button
                type="button"
                onClick={clearSelection}
                className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                disabled={selectedValues.length === 0}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-blue-600 hover:underline"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
